import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Users, DollarSign, Link2, UserPlus, Calendar, ChevronDown, TrendingUp, Wallet, Calculator, Minus, Plus, BarChart3 } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliatePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('income');
  const [calcParents, setCalcParents] = useState(30);
  const [calcPlan, setCalcPlan] = useState('basic');
  const isEN = language === 'en';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchData();
  }, [token]);

  useEffect(() => {
    if (affiliate && token) {
      fetchEarnings(selectedMonth || undefined);
    }
  }, [affiliate, selectedMonth]);

  const fetchData = async () => {
    try {
      const [affRes, refRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/affiliate/me`, { headers }),
        fetch(`${BACKEND_URL}/api/affiliate/referrals`, { headers }).catch(() => ({ ok: false }))
      ]);
      if (affRes.ok) { const d = await affRes.json(); setAffiliate(d); }
      if (refRes.ok) { setReferrals(await refRes.json()); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchEarnings = async (month) => {
    try {
      const url = month
        ? `${BACKEND_URL}/api/affiliate/earnings?month=${month}`
        : `${BACKEND_URL}/api/affiliate/earnings`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setEarnings(data);
        if (!selectedMonth && data.month) setSelectedMonth(data.month);
      }
    } catch (e) { console.error(e); }
  };

  const applyAffiliate = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/affiliate/apply`, { method: 'POST', headers, body: JSON.stringify({}) });
      if (res.ok) fetchData();
    } catch (e) { console.error(e); }
  };

  const copyLink = () => {
    if (!affiliate) return;
    const link = `https://pantass.com/register?ref=${affiliate.code}`;
    navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const planPrices = { basic: 19.90, pro: 49.90, school: 199 };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(isEN ? 'en-MY' : 'ms-MY', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(isEN ? 'en-MY' : 'ms-MY', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{isEN ? 'Affiliate Program' : 'Program Affiliate'}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {loading ? <div className="lb-loading"><div className="lb-spinner" /></div> : !affiliate ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="aff-join-hero">
              <div className="aff-join-badge">{isEN ? 'AFFILIATE PROGRAM' : 'PROGRAM AFFILIATE'}</div>
              <h2 className="aff-join-title">{isEN ? 'Teachers, Earn RM5,000+/month by Sharing PANTASS' : 'Cikgu, Jana RM5,000+/bulan dengan Kongsi PANTASS'}</h2>
              <p className="aff-join-desc">{isEN ? 'Join our affiliate program and earn 30% recurring commission for every parent who subscribes through your link. Share once, earn every month!' : 'Sertai program affiliate dan dapatkan 30% komisen berulang untuk setiap ibu bapa yang langgan melalui link anda. Kongsi sekali, dapat setiap bulan!'}</p>
            </div>

            {/* Income Calculator */}
            <div className="aff-calc" data-testid="affiliate-calculator">
              <h3 className="aff-calc-title">
                <Calculator size={18} />
                {isEN ? 'Income Calculator' : 'Kalkulator Pendapatan'}
              </h3>

              {/* Plan Selector */}
              <div className="aff-calc-plan-row">
                <span className="aff-calc-label">{isEN ? 'Plan' : 'Pelan'}:</span>
                <div className="aff-calc-plan-btns">
                  {[
                    { id: 'basic', label: 'Basic', price: 19.90, color: '#3b82f6' },
                    { id: 'pro', label: 'Pro', price: 49.90, color: '#E85D3A' },
                    { id: 'school', label: 'School', price: 199, color: '#009688' },
                  ].map(p => (
                    <button key={p.id}
                      className={`aff-calc-plan-btn ${calcPlan === p.id ? 'aff-calc-plan-active' : ''}`}
                      style={calcPlan === p.id ? { background: p.color, borderColor: p.color } : {}}
                      onClick={() => setCalcPlan(p.id)}
                      data-testid={`calc-plan-${p.id}`}>
                      {p.label}
                      <span className="aff-calc-plan-price">RM{p.price}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Parents Slider */}
              <div className="aff-calc-slider-row">
                <span className="aff-calc-label">{isEN ? 'Parents who subscribe' : 'Ibu bapa yang langgan'}:</span>
                <div className="aff-calc-slider-controls">
                  <button className="aff-calc-minus" onClick={() => setCalcParents(Math.max(1, calcParents - 5))} data-testid="calc-minus">
                    <Minus size={16} />
                  </button>
                  <input
                    type="range" min="1" max="500" value={calcParents}
                    onChange={(e) => setCalcParents(Number(e.target.value))}
                    className="aff-calc-range"
                    data-testid="calc-slider"
                  />
                  <button className="aff-calc-plus" onClick={() => setCalcParents(Math.min(500, calcParents + 5))} data-testid="calc-plus">
                    <Plus size={16} />
                  </button>
                </div>
                <div className="aff-calc-parents-num" data-testid="calc-parents-count">
                  {calcParents} {isEN ? 'parents' : 'ibu bapa'}
                </div>
              </div>

              {/* Result */}
              <div className="aff-calc-result" data-testid="calc-result">
                <div className="aff-calc-formula">
                  {calcParents} {isEN ? 'parents' : 'ibu bapa'} x RM{planPrices[calcPlan]} x 30%
                </div>
                <div className="aff-calc-equals">=</div>
                <div className="aff-calc-amount" data-testid="calc-amount">
                  RM{(calcParents * planPrices[calcPlan] * 0.3).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<span className="aff-calc-period">/{isEN ? 'month' : 'bulan'}</span>
                </div>
                <div className="aff-calc-annual">
                  = RM{(calcParents * planPrices[calcPlan] * 0.3 * 12).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{isEN ? 'year' : 'tahun'}
                </div>
              </div>

              {/* Quick Examples */}
              <div className="aff-calc-examples">
                {[10, 50, 100, 200].map(n => {
                  const income = n * planPrices[calcPlan] * 0.3;
                  return (
                    <button key={n} className="aff-calc-example" onClick={() => setCalcParents(n)}
                      data-testid={`calc-example-${n}`}>
                      <span className="aff-calc-ex-parents">{n}</span>
                      <span className="aff-calc-ex-amount">RM{income.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <motion.button className="aff-join-btn" onClick={applyAffiliate}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} data-testid="affiliate-join-btn">
              {isEN ? 'Join Affiliate Program — Free' : 'Sertai Program Affiliate — Percuma'} &rarr;
            </motion.button>

            <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: '16px' }}>&larr; {isEN ? 'Back to Home' : 'Kembali'}</button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Referral Link */}
            <div className="affiliate-link-card">
              <h3>{isEN ? 'Your Referral Link' : 'Link Rujukan Anda'}</h3>
              <div className="affiliate-link-box">
                <input readOnly value={`https://pantass.com/register?ref=${affiliate.code}`} className="affiliate-link-input" />
                <button className="affiliate-copy-btn" onClick={copyLink} data-testid="affiliate-copy-btn">
                  <Copy size={16} /> {copied ? (isEN ? 'Copied!' : 'Disalin!') : (isEN ? 'Copy' : 'Salin')}
                </button>
              </div>
              <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>{isEN ? 'Share this link on WhatsApp, social media, or anywhere!' : 'Kongsi link ini di WhatsApp, media sosial, atau di mana sahaja!'}</p>
            </div>

            {/* Stats */}
            <div className="stats-grid" style={{ marginTop: '20px' }}>
              <div className="stat-card stat-card-blue"><UserPlus size={24} /><div className="stat-card-value">{affiliate.total_signups || 0}</div><div className="stat-card-label">{isEN ? 'Total Signups' : 'Jumlah Pendaftaran'}</div></div>
              <div className="stat-card stat-card-green"><Users size={24} /><div className="stat-card-value">{affiliate.total_paid_referrals || 0}</div><div className="stat-card-label">{isEN ? 'Paid Subscribers' : 'Pelanggan Berbayar'}</div></div>
              <div className="stat-card stat-card-orange"><DollarSign size={24} /><div className="stat-card-value">RM{affiliate.total_earnings || 0}</div><div className="stat-card-label">{isEN ? 'Total Earnings' : 'Jumlah Pendapatan'}</div></div>
              <div className="stat-card stat-card-purple"><Link2 size={24} /><div className="stat-card-value">{affiliate.code}</div><div className="stat-card-label">{isEN ? 'Your Code' : 'Kod Anda'}</div></div>
            </div>

            {/* Tabs: Income / Referrals / Calculator */}
            <div className="aff-tabs" data-testid="affiliate-tabs">
              <button className={`aff-tab ${activeTab === 'income' ? 'aff-tab-active' : ''}`}
                onClick={() => setActiveTab('income')} data-testid="aff-tab-income">
                <Wallet size={14} />
                {isEN ? 'Income' : 'Pendapatan'}
              </button>
              <button className={`aff-tab ${activeTab === 'referrals' ? 'aff-tab-active' : ''}`}
                onClick={() => setActiveTab('referrals')} data-testid="aff-tab-referrals">
                <Users size={14} />
                {isEN ? 'Referrals' : 'Rujukan'}
              </button>
              <button className={`aff-tab ${activeTab === 'calculator' ? 'aff-tab-active' : ''}`}
                onClick={() => setActiveTab('calculator')} data-testid="aff-tab-calculator">
                <Calculator size={14} />
                {isEN ? 'Calculator' : 'Kalkulator'}
              </button>
              <button className={`aff-tab ${activeTab === 'analytics' ? 'aff-tab-active' : ''}`}
                onClick={() => setActiveTab('analytics')} data-testid="aff-tab-analytics">
                <BarChart3 size={14} />
                {isEN ? 'Analytics' : 'Analitik'}
              </button>
            </div>

            {/* Income Tab */}
            {activeTab === 'income' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="affiliate-income-section">
                {/* Monthly Filter */}
                <div className="aff-month-filter" data-testid="affiliate-month-filter">
                  <Calendar size={16} />
                  <select
                    className="aff-month-select"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    data-testid="affiliate-month-select"
                  >
                    {earnings?.available_months?.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>

                {/* Monthly Summary */}
                <div className="aff-monthly-summary" data-testid="affiliate-monthly-total">
                  <div className="aff-monthly-label">{isEN ? 'Monthly Income' : 'Pendapatan Bulanan'}</div>
                  <div className="aff-monthly-value">RM{earnings?.monthly_total?.toFixed(2) || '0.00'}</div>
                  <div className="aff-monthly-sub">
                    {earnings?.daily?.length || 0} {isEN ? 'day(s) with income' : 'hari ada pendapatan'}
                  </div>
                </div>

                {/* Daily Breakdown */}
                <h3 className="aff-section-title" data-testid="affiliate-daily-title">
                  <TrendingUp size={16} />
                  {isEN ? 'Daily Income' : 'Pendapatan Harian'}
                </h3>

                {!earnings?.daily?.length ? (
                  <div className="lb-empty" data-testid="affiliate-no-income">
                    {isEN ? 'No income this month yet. Share your link to start earning!' : 'Tiada pendapatan bulan ini. Kongsi link anda untuk mula menjana!'}
                  </div>
                ) : (
                  <div className="aff-daily-list" data-testid="affiliate-daily-list">
                    {earnings.daily.map((day, i) => (
                      <div key={i} className="aff-daily-item" data-testid={`affiliate-daily-${i}`}>
                        <div className="aff-daily-left">
                          <div className="aff-daily-date">{formatDay(day.date)}</div>
                          <div className="aff-daily-refs">
                            {day.referrals.map((ref, j) => (
                              <span key={j} className="aff-daily-ref-tag">
                                {ref.name} <span className="aff-daily-ref-plan">{ref.plan.toUpperCase()}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="aff-daily-amount">+RM{day.income.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Referrals Tab */}
            {activeTab === 'referrals' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="affiliate-referrals-section">
                <h3 className="aff-section-title">
                  <Users size={16} />
                  {isEN ? 'Your Referrals' : 'Rujukan Anda'}
                </h3>
                {referrals.length === 0 ? (
                  <div className="lb-empty">{isEN ? 'No referrals yet. Share your link to start earning!' : 'Tiada rujukan lagi. Kongsi link anda untuk mula menjana!'}</div>
                ) : (
                  <div className="lb-table-container"><table className="lb-table"><thead><tr>
                    <th>#</th>
                    <th>{isEN ? 'Name' : 'Nama'}</th>
                    <th>{isEN ? 'Plan' : 'Pelan'}</th>
                    <th>{isEN ? 'Date' : 'Tarikh'}</th>
                    <th>{isEN ? 'Commission' : 'Komisen'}</th>
                  </tr></thead><tbody>
                    {referrals.map((ref, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td className="lb-team-name">{ref.name}</td>
                        <td><span className={`tournament-status tournament-status-${ref.plan === 'free' ? 'active' : 'completed'}`}>{(ref.plan || 'free').toUpperCase()}</span></td>
                        <td style={{ fontSize: '12px', color: '#64748b' }}>{formatDate(ref.created_at)}</td>
                        <td className="lb-total-score">{ref.plan !== 'free' ? `RM${((planPrices[ref.plan] || 0) * 0.3).toFixed(2)}/bln` : '-'}</td>
                      </tr>
                    ))}
                  </tbody></table></div>
                )}
              </motion.div>
            )}

            {/* Calculator Tab */}
            {activeTab === 'calculator' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="affiliate-calc-tab">
                <div className="aff-calc" data-testid="affiliate-calculator-dash">
                  <h3 className="aff-calc-title">
                    <Calculator size={18} />
                    {isEN ? 'Income Calculator' : 'Kalkulator Pendapatan'}
                  </h3>
                  <div className="aff-calc-plan-row">
                    <span className="aff-calc-label">{isEN ? 'Plan' : 'Pelan'}:</span>
                    <div className="aff-calc-plan-btns">
                      {[
                        { id: 'basic', label: 'Basic', price: 19.90, color: '#3b82f6' },
                        { id: 'pro', label: 'Pro', price: 49.90, color: '#E85D3A' },
                        { id: 'school', label: 'School', price: 199, color: '#009688' },
                      ].map(p => (
                        <button key={p.id}
                          className={`aff-calc-plan-btn ${calcPlan === p.id ? 'aff-calc-plan-active' : ''}`}
                          style={calcPlan === p.id ? { background: p.color, borderColor: p.color } : {}}
                          onClick={() => setCalcPlan(p.id)}>
                          {p.label}
                          <span className="aff-calc-plan-price">RM{p.price}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="aff-calc-slider-row">
                    <span className="aff-calc-label">{isEN ? 'Parents who subscribe' : 'Ibu bapa yang langgan'}:</span>
                    <div className="aff-calc-slider-controls">
                      <button className="aff-calc-minus" onClick={() => setCalcParents(Math.max(1, calcParents - 5))}><Minus size={16} /></button>
                      <input type="range" min="1" max="500" value={calcParents} onChange={(e) => setCalcParents(Number(e.target.value))} className="aff-calc-range" />
                      <button className="aff-calc-plus" onClick={() => setCalcParents(Math.min(500, calcParents + 5))}><Plus size={16} /></button>
                    </div>
                    <div className="aff-calc-parents-num">{calcParents} {isEN ? 'parents' : 'ibu bapa'}</div>
                  </div>
                  <div className="aff-calc-result">
                    <div className="aff-calc-formula">{calcParents} {isEN ? 'parents' : 'ibu bapa'} x RM{planPrices[calcPlan]} x 30%</div>
                    <div className="aff-calc-equals">=</div>
                    <div className="aff-calc-amount">
                      RM{(calcParents * planPrices[calcPlan] * 0.3).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<span className="aff-calc-period">/{isEN ? 'month' : 'bulan'}</span>
                    </div>
                    <div className="aff-calc-annual">
                      = RM{(calcParents * planPrices[calcPlan] * 0.3 * 12).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{isEN ? 'year' : 'tahun'}
                    </div>
                  </div>
                  <div className="aff-calc-examples">
                    {[10, 50, 100, 200].map(n => {
                      const income = n * planPrices[calcPlan] * 0.3;
                      return (
                        <button key={n} className="aff-calc-example" onClick={() => setCalcParents(n)}>
                          <span className="aff-calc-ex-parents">{n}</span>
                          <span className="aff-calc-ex-amount">RM{income.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="affiliate-analytics-section">
                {(() => {
                  // Build chart data from referrals and earnings
                  const totalSignups = affiliate?.total_signups || 0;
                  const paidRefs = affiliate?.total_paid_referrals || 0;
                  const freeRefs = Math.max(0, totalSignups - paidRefs);
                  const totalEarnings = affiliate?.total_earnings || 0;

                  // Referral plan breakdown
                  const planCount = { basic: 0, pro: 0, school: 0, free: 0 };
                  referrals.forEach(r => { planCount[r.plan || 'free'] = (planCount[r.plan || 'free'] || 0) + 1; });

                  // Donut: Conversion Rate
                  const conversionData = [
                    { name: isEN ? 'Paid' : 'Berbayar', value: Math.max(paidRefs, 1), color: '#22c55e' },
                    { name: isEN ? 'Free' : 'Percuma', value: Math.max(freeRefs, 1), color: '#e2e8f0' },
                  ];
                  const conversionRate = totalSignups > 0 ? Math.round((paidRefs / totalSignups) * 100) : 0;

                  // Pie: Plan Distribution
                  const planData = [
                    { name: 'Basic', value: Math.max(planCount.basic, 0), color: '#3b82f6' },
                    { name: 'Pro', value: Math.max(planCount.pro, 0), color: '#E85D3A' },
                    { name: 'School', value: Math.max(planCount.school, 0), color: '#009688' },
                  ].filter(d => d.value > 0);
                  // If no paid referrals, show placeholder
                  const hasPaidData = planData.length > 0;
                  const planDataFinal = hasPaidData ? planData : [
                    { name: 'Basic', value: 3, color: '#3b82f6' },
                    { name: 'Pro', value: 2, color: '#E85D3A' },
                    { name: 'School', value: 1, color: '#009688' },
                  ];

                  // Bar: Monthly earnings (projected if no data)
                  const monthNames = isEN
                    ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
                    : ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun'];
                  const now = new Date();
                  const currentMonth = now.getMonth();
                  const monthlyBarData = monthNames.map((name, i) => {
                    if (i < currentMonth) return { name, income: 0 };
                    if (i === currentMonth) return { name, income: totalEarnings };
                    return { name, income: 0 };
                  });
                  // If all zeros, show motivational projection
                  const hasRealData = monthlyBarData.some(d => d.income > 0);
                  const projectedBarData = hasRealData ? monthlyBarData : monthNames.map((name, i) => ({
                    name, income: Math.round((i + 1) * 89.55 * (1 + i * 0.3)),
                  }));

                  // Area: Growth projection (next 6 months)
                  const growthData = Array.from({ length: 6 }, (_, i) => {
                    const projRefs = Math.max(totalSignups, 5) * (1 + (i + 1) * 0.4);
                    const projIncome = projRefs * 0.6 * 19.90 * 0.3;
                    return {
                      name: isEN ? `Month ${i + 1}` : `Bulan ${i + 1}`,
                      referrals: Math.round(projRefs),
                      income: Math.round(projIncome),
                    };
                  });

                  // Commission per plan bar
                  const commissionBarData = [
                    { name: 'Basic', commission: 5.97, color: '#3b82f6', subscribers: planCount.basic || 0 },
                    { name: 'Pro', commission: 14.97, color: '#E85D3A', subscribers: planCount.pro || 0 },
                    { name: 'School', commission: 59.70, color: '#009688', subscribers: planCount.school || 0 },
                  ];

                  return (
                    <>
                      {/* Motivational Header */}
                      <div className="aff-analytics-header" data-testid="analytics-header">
                        <div className="aff-analytics-emoji">{totalEarnings > 0 ? '\uD83D\uDD25' : '\uD83D\uDE80'}</div>
                        <h3>{isEN ? (totalEarnings > 0 ? 'Your Earnings Are Growing!' : 'Your Income Journey Starts Here') : (totalEarnings > 0 ? 'Pendapatan Anda Meningkat!' : 'Perjalanan Pendapatan Anda Bermula')}</h3>
                        <p>{isEN ? (totalEarnings > 0 ? 'Keep sharing to unlock more income milestones!' : 'Share your link with parents and watch these charts come alive!') : (totalEarnings > 0 ? 'Teruskan berkongsi untuk buka milestone pendapatan!' : 'Kongsi link ke ibu bapa dan lihat chart ini bergerak!')}</p>
                      </div>

                      {/* Charts Grid */}
                      <div className="aff-charts-grid">

                        {/* Donut: Conversion Rate */}
                        <div className="aff-chart-card" data-testid="chart-conversion">
                          <h4 className="aff-chart-title">{isEN ? 'Conversion Rate' : 'Kadar Penukaran'}</h4>
                          <div className="aff-chart-donut-wrap">
                            <ResponsiveContainer width="100%" height={180}>
                              <PieChart>
                                <Pie data={conversionData} cx="50%" cy="50%" innerRadius={55} outerRadius={75} dataKey="value" startAngle={90} endAngle={-270} strokeWidth={0}>
                                  {conversionData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                                </Pie>
                                <Tooltip formatter={(val, name) => [`${val}`, name]} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="aff-donut-center">
                              <span className="aff-donut-pct">{conversionRate}%</span>
                              <span className="aff-donut-label">{isEN ? 'Converted' : 'Berjaya'}</span>
                            </div>
                          </div>
                          <div className="aff-chart-legend">
                            <span className="aff-legend-item"><span className="aff-legend-dot" style={{ background: '#22c55e' }} />{isEN ? 'Paid' : 'Berbayar'}: {paidRefs}</span>
                            <span className="aff-legend-item"><span className="aff-legend-dot" style={{ background: '#e2e8f0' }} />{isEN ? 'Free' : 'Percuma'}: {freeRefs}</span>
                          </div>
                        </div>

                        {/* Pie: Plan Distribution */}
                        <div className="aff-chart-card" data-testid="chart-plan-dist">
                          <h4 className="aff-chart-title">{isEN ? 'Subscriber Plans' : 'Pelan Pelanggan'}</h4>
                          {!hasPaidData && <span className="aff-chart-sample">{isEN ? 'Sample Data' : 'Data Contoh'}</span>}
                          <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                              <Pie data={planDataFinal} cx="50%" cy="50%" outerRadius={75} dataKey="value" strokeWidth={2} stroke="#fff">
                                {planDataFinal.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                              </Pie>
                              <Tooltip formatter={(val, name) => [`${val} ${isEN ? 'subscribers' : 'pelanggan'}`, name]} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="aff-chart-legend">
                            {planDataFinal.map((d, i) => (
                              <span key={i} className="aff-legend-item"><span className="aff-legend-dot" style={{ background: d.color }} />{d.name}: {d.value}</span>
                            ))}
                          </div>
                        </div>

                        {/* Bar: Commission Per Plan */}
                        <div className="aff-chart-card aff-chart-wide" data-testid="chart-commission">
                          <h4 className="aff-chart-title">{isEN ? 'Commission Per Plan' : 'Komisen Setiap Pelan'}</h4>
                          <p className="aff-chart-subtitle">{isEN ? 'Your monthly commission per subscriber' : 'Komisen bulanan anda setiap pelanggan'}</p>
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={commissionBarData} barSize={48}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                              <XAxis dataKey="name" tick={{ fontSize: 13, fontWeight: 700 }} />
                              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `RM${v}`} />
                              <Tooltip formatter={(val) => [`RM${val.toFixed(2)}`, isEN ? 'Commission' : 'Komisen']} />
                              <Bar dataKey="commission" radius={[8, 8, 0, 0]}>
                                {commissionBarData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                          <div className="aff-commission-tags">
                            {commissionBarData.map((d, i) => (
                              <div key={i} className="aff-commission-tag" style={{ borderColor: d.color }}>
                                <span style={{ color: d.color, fontWeight: 900 }}>RM{d.commission}</span>
                                <span>{isEN ? '/subscriber/month' : '/pelanggan/bulan'}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Area: Growth Projection */}
                        <div className="aff-chart-card aff-chart-wide" data-testid="chart-growth">
                          <h4 className="aff-chart-title">{isEN ? 'Income Growth Projection' : 'Unjuran Pertumbuhan Pendapatan'}</h4>
                          <p className="aff-chart-subtitle">{isEN ? 'Projected income if you keep sharing' : 'Unjuran pendapatan jika terus berkongsi'}</p>
                          <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={growthData}>
                              <defs>
                                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#009688" stopOpacity={0.3} />
                                  <stop offset="100%" stopColor="#009688" stopOpacity={0.02} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                              <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 600 }} />
                              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `RM${v}`} />
                              <Tooltip formatter={(val, name) => [name === 'income' ? `RM${val}` : val, name === 'income' ? (isEN ? 'Income' : 'Pendapatan') : (isEN ? 'Referrals' : 'Rujukan')]} />
                              <Area type="monotone" dataKey="income" stroke="#009688" fill="url(#incomeGrad)" strokeWidth={3} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Monthly Earnings Bar */}
                        <div className="aff-chart-card aff-chart-wide" data-testid="chart-monthly-bar">
                          <h4 className="aff-chart-title">{isEN ? 'Monthly Earnings' : 'Pendapatan Bulanan'}</h4>
                          {!hasRealData && <span className="aff-chart-sample">{isEN ? 'Projected' : 'Unjuran'}</span>}
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={projectedBarData} barSize={36}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                              <XAxis dataKey="name" tick={{ fontSize: 13, fontWeight: 700 }} />
                              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `RM${v}`} />
                              <Tooltip formatter={(val) => [`RM${val.toFixed(2)}`, isEN ? 'Income' : 'Pendapatan']} />
                              <Bar dataKey="income" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                      </div>

                      {/* Milestone Targets */}
                      <div className="aff-milestones" data-testid="analytics-milestones">
                        <h4 className="aff-chart-title" style={{ marginBottom: '12px' }}>{isEN ? 'Income Milestones' : 'Milestone Pendapatan'}</h4>
                        {[
                          { target: 100, icon: '\uD83C\uDFC5', label: isEN ? 'Starter' : 'Pemula' },
                          { target: 500, icon: '\uD83E\uDD47', label: isEN ? 'Rising Star' : 'Bintang Naik' },
                          { target: 1000, icon: '\uD83D\uDCB0', label: isEN ? 'Top Earner' : 'Penjana Utama' },
                          { target: 5000, icon: '\uD83D\uDC8E', label: isEN ? 'Diamond Affiliate' : 'Affiliate Berlian' },
                        ].map((m, i) => {
                          const progress = Math.min((totalEarnings / m.target) * 100, 100);
                          const achieved = totalEarnings >= m.target;
                          return (
                            <div key={i} className={`aff-milestone ${achieved ? 'aff-milestone-done' : ''}`} data-testid={`milestone-${m.target}`}>
                              <span className="aff-milestone-icon">{achieved ? '\u2705' : m.icon}</span>
                              <div className="aff-milestone-info">
                                <div className="aff-milestone-label">{m.label} — RM{m.target.toLocaleString()}/{isEN ? 'month' : 'bulan'}</div>
                                <div className="aff-milestone-bar"><div className="aff-milestone-fill" style={{ width: `${progress}%` }} /></div>
                              </div>
                              <span className="aff-milestone-pct">{Math.round(progress)}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            )}

            <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: '20px' }}>&larr; {isEN ? 'Back to Home' : 'Kembali'}</button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
